/**
 * Visual presets picked in the Identity step. This file is a thin CSS-friendly
 * *view* over the canonical theming registry (`theming/registry.ts`) so the
 * 2D picker + preview stay aligned with the live 3D scene without duplicating
 * palette data.
 *
 * IDs surface directly from the registry — a sky preset ID is a
 * `SkyboxTheme` (e.g. `sky` → Open Sky, `farm` → Homestead) and a plinth
 * preset ID is a `PlinthMaterialId` (`'stone'`, `'obsidian'`, …). That way a
 * saved draft in the wizard maps 1-to-1 onto what the 3D scene renders when
 * the board finally launches — no lookup tables needed later.
 */

import {
  SKYBOXES,
  PLINTH_MATERIALS,
  type SkyboxTheme,
  type PlinthMaterialId,
} from '@/features/lobby/theming/registry';

// ---------------------------------------------------------------------------
// Sky presets — CSS view of the skybox registry for the 2D picker + preview.
// ---------------------------------------------------------------------------

export interface SkyboxPreset {
  id: SkyboxTheme;
  name: string;
  /** Short line under the name — same as registry sublabel. */
  sublabel: string;
  /** CSS background string applied to the preview stage and picker thumb. */
  background: string;
  /** Secondary halo/star colour. Sourced from the 3D scene's key light so
   *  the 2D and 3D renders feel like the same atmosphere. */
  accent: string;
}

export const SKYBOX_PRESETS: readonly SkyboxPreset[] = SKYBOXES.map((s) => ({
  id: s.id,
  name: s.name,
  sublabel: s.sublabel,
  background: s.thumbnail,
  accent: s.keyLight,
}));

/**
 * Skybox presets surfaced to community board creators in the launch
 * wizard. Drops every `tier: 'premium'` entry from `SKYBOX_PRESETS` so
 * 1-of-1 chamber skies (e.g. Sanctum / Boardroom) stay reserved for
 * platform-owned grids — the dev `SkyboxPickerPanel` still iterates the
 * full registry for QA preview.
 *
 * Mirrors how premium plinth materials will be filtered when those
 * arrive (see `PlinthMaterialTier`); same `tier` flag, same gating
 * pattern, single source of truth in the registry.
 */
export const PUBLIC_SKYBOX_PRESETS: readonly SkyboxPreset[] = SKYBOXES.filter(
  (s) => s.tier === 'vanilla',
).map((s) => ({
  id: s.id,
  name: s.name,
  sublabel: s.sublabel,
  background: s.thumbnail,
  accent: s.keyLight,
}));

/**
 * Sky preview lookup. Returns:
 *   - the preset for `id` when it's a registered skybox;
 *   - `null` when the user picked "None" (or hasn't picked yet) so the
 *     preview can render a neutral void instead of forcing a default;
 *   - `null` if the id isn't found in the registry (treated the same as
 *     None — the wizard's UI prevents this in practice).
 *
 * The wizard previously coerced "None" into Open Sky so the create flow
 * had something to persist, but the launch path now sends a `'none'`
 * sentinel directly to the metadata API — the preview no longer needs
 * to lie about the user's choice.
 */
export function effectiveSkyboxPreset(
  id: SkyboxTheme | null | undefined,
): SkyboxPreset | null {
  if (id == null) return null;
  return SKYBOX_PRESETS.find((s) => s.id === id) ?? null;
}

// ---------------------------------------------------------------------------
// Plinth presets — CSS view of the plinth material registry.
//
// Plinth IDs are intentionally aligned with `PlinthMaterialId` so the wizard
// and the 3D scene share a single vocabulary. The 2D preview gets a compact
// CSS gradient synthesised from the material's top/body/socle palette which
// reads well in a small square without needing a bespoke texture.
// ---------------------------------------------------------------------------

export interface PlinthPreset {
  id: PlinthMaterialId;
  name: string;
  /** Short line under the name — same as registry sublabel.
   *  Surfaces in the launch preview's bottom-right label block (the
   *  visual mirror of the skybox label on the bottom-left). */
  sublabel: string;
  /** CSS background for the surface of the plinth. */
  background: string;
  /** Edge / trim accent for the plinth rim. */
  edge: string;
  /** Small glyph carved into the stone face in the 2D picker. */
  glyph?: string;
}

// Stable per-material decoration. Picker-only — the 3D plinth stays clean.
const MATERIAL_GLYPHS: Record<PlinthMaterialId, string> = {
  stone: '△',
  obsidian: '❖',
  marble: '✦',
  sandstone: '◈',
  slate: '▦',
  wood: '❧',
  velvet: '♛',
};

export const PLINTH_PRESETS: readonly PlinthPreset[] = PLINTH_MATERIALS.map(
  (m) => ({
    id: m.id,
    name: m.name,
    sublabel: m.sublabel,
    // Top → body → socle reads as stone catching light from above.
    background: `linear-gradient(135deg, ${m.top} 0%, ${m.body} 55%, ${m.socle} 100%)`,
    edge: m.trim,
    glyph: MATERIAL_GLYPHS[m.id],
  }),
);

/**
 * Plinth presets surfaced to community board creators + onboarding
 * players. Drops every `tier: 'premium'` entry from `PLINTH_PRESETS` so
 * 1-of-1 cabal materials (e.g. velvet drape) stay reserved for
 * platform-owned grids — the dev / debug surfaces still iterate the
 * full registry for QA preview and the renderer can resolve any stored
 * id by looking it up against the canonical `PLINTH_MATERIAL_BY_ID`.
 *
 * Mirrors `PUBLIC_SKYBOX_PRESETS`: same `tier` flag, same gating
 * pattern, single source of truth in the plinths registry.
 */
export const PUBLIC_PLINTH_PRESETS: readonly PlinthPreset[] =
  PLINTH_MATERIALS.filter((m) => m.tier === 'vanilla').map((m) => ({
    id: m.id,
    name: m.name,
    sublabel: m.sublabel,
    background: `linear-gradient(135deg, ${m.top} 0%, ${m.body} 55%, ${m.socle} 100%)`,
    edge: m.trim,
    glyph: MATERIAL_GLYPHS[m.id],
  }));

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export function skyboxById(id: string | null): SkyboxPreset | null {
  if (!id) return null;
  return SKYBOX_PRESETS.find((s) => s.id === id) ?? null;
}

export function plinthById(id: string | null): PlinthPreset | null {
  if (!id) return null;
  return PLINTH_PRESETS.find((p) => p.id === id) ?? null;
}

